import React, { useState, useEffect } from 'react';
import { Settings, Database, RefreshCw, Trash2, AlertTriangle, CheckCircle, Info, Package, Cpu, Zap } from 'lucide-react';
import { simulatorsApi, getApiBaseUrl } from '@/services/api';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import Modal from '@/components/common/Modal';

interface LLMConfig {
  active_provider: string;
  active_model: string;
  available_providers: {
    [key: string]: {
      configured: boolean;
      models: { id: string; name: string }[];
    };
  };
}

const AdminPage: React.FC = () => {
  const [resetLoading, setResetLoading] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  const API_BASE = getApiBaseUrl();
  const [resetStatus, setResetStatus] = useState<{
    type: 'success' | 'error' | 'warning' | 'info' | null;
    message: string;
  }>({ type: null, message: '' });
  const [clearStatus, setClearStatus] = useState<{
    type: 'success' | 'error' | 'warning' | 'info' | null;
    message: string;
  }>({ type: null, message: '' });
  const [statsData, setStatsData] = useState<any>(null);

  // LLM Configuration state
  const [llmConfig, setLlmConfig] = useState<LLMConfig | null>(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [llmSaving, setLlmSaving] = useState(false);
  const [llmTesting, setLlmTesting] = useState(false);
  const [llmTestResult, setLlmTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [modal, setModal] = useState<{
    open: boolean;
    variant: 'success' | 'error' | 'info';
    title?: string;
    message: string;
  }>({ open: false, variant: 'info', message: '' });

  // Load LLM config on mount
  useEffect(() => {
    loadLlmConfig();
  }, []);

  const loadLlmConfig = async () => {
    setLlmLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/llm-config`);
      if (res.ok) {
        const result = await res.json();
        const cfg: LLMConfig = result.data;
        setLlmConfig(cfg);
        setSelectedProvider(cfg.active_provider);
        setSelectedModel(cfg.active_model);
      }
    } catch (e) {
      console.error('Failed to load LLM config:', e);
    } finally {
      setLlmLoading(false);
    }
  };

  const handleSaveLlmConfig = async () => {
    setLlmSaving(true);
    setLlmTestResult(null);
    try {
      const res = await fetch(`${API_BASE}/admin/llm-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: selectedProvider, model: selectedModel }),
      });
      const result = await res.json();
      if (result.success) {
        setLlmConfig(result.data);
        setModal({ open: true, variant: 'success', title: 'LLM Updated', message: result.message });
      } else {
        setModal({ open: true, variant: 'error', title: 'Update Failed', message: result.detail || 'Failed to update' });
      }
    } catch (e) {
      setModal({ open: true, variant: 'error', title: 'Error', message: e instanceof Error ? e.message : 'Unknown error' });
    } finally {
      setLlmSaving(false);
    }
  };

  const handleTestLlm = async () => {
    setLlmTesting(true);
    setLlmTestResult(null);
    try {
      const res = await fetch(`${API_BASE}/admin/llm-test`, { method: 'POST' });
      const result = await res.json();
      setLlmTestResult({ success: result.success, message: result.message });
    } catch (e) {
      setLlmTestResult({ success: false, message: e instanceof Error ? e.message : 'Connection failed' });
    } finally {
      setLlmTesting(false);
    }
  };

  const availableModels = llmConfig?.available_providers[selectedProvider]?.models || [];
  const providerHasKey = llmConfig?.available_providers[selectedProvider]?.configured ?? false;

  const handleResetData = async () => {
    setResetLoading(true);
    setResetStatus({ type: null, message: '' });

    try {
      // Use admin endpoint to reset with scenario-based demo data (DT-FINANCE-001, DT-TAX-005, etc.)
      const response = await fetch(`${API_BASE}/admin/reset-demo-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Reset failed: ${response.statusText}`);
      }

      const result = await response.json();

      setResetStatus({
        type: 'success',
        message: `Successfully reset all data! Created ${result.data.orders_created} orders with ${result.data.discrepancies_injected} discrepancies across ${result.data.scenarios.length} DT scenarios. (DT-FINANCE-001, DT-TAX-005, etc.)`
      });

    } catch (error) {
      setResetStatus({
        type: 'error',
        message: `Failed to reset data: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setResetLoading(false);
    }
  };

  const handleClearAllData = async () => {
    setClearLoading(true);
    setClearStatus({ type: null, message: '' });

    try {
      const response = await fetch(`${API_BASE}/admin/clear-all-data`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setClearStatus({
        type: 'warning',
        message: 'All data has been permanently deleted! This action cannot be undone.'
      });

    } catch (error) {
      setClearStatus({
        type: 'error',
        message: `Failed to clear data: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setClearLoading(false);
    }
  };

  const handleViewStats = async () => {
    setStatsLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/admin/data-stats`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setStatsData(result.data);

    } catch (error) {
      console.error('Failed to fetch stats:', error);
      setStatsData({ error: `Failed to fetch statistics: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setStatsLoading(false);
    }
  };

  const handleCreateDTOrders = async () => {
    setResetLoading(true);
    setResetStatus({ type: null, message: '' });

    try {
      // Call the new DT orders endpoint  
      const response = await fetch(`${API_BASE}/admin/create-dt-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      setResetStatus({
        type: 'success',
        message: `Successfully created ${result.data?.orders_created || 'new'} realistic DT orders with ${result.data?.discrepancies_injected || 'multiple'} discrepancies. Ready for AI demo!`
      });

    } catch (error) {
      setResetStatus({
        type: 'error',
        message: `Failed to create DT orders: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setResetLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (resetStatus.type || clearStatus.type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'info':
        return <Info className="h-5 w-5 text-blue-600" />;
      default:
        return null;
    }
  };

  const getStatusBgColor = () => {
    const status = resetStatus.type || clearStatus.type;
    switch (status) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-700';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-700';
      default:
        return '';
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <Settings className="h-8 w-8 text-gray-700" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-gray-600">System administration and data management</p>
          </div>
        </div>
      </div>

      {/* Data Management Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Database className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Data Management</h2>
              <p className="text-sm text-gray-600">Reset and regenerate system data for demo purposes</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800">Reset Demo Data</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  This action will clear all existing orders and audit trail data, then create fresh realistic orders 
                  with cross-system discrepancies based on real DT incident patterns. Perfect for restarting demos.
                </p>
              </div>
            </div>
          </div>

          {/* Status Messages */}
          {resetStatus.message && (
            <div className={`border rounded-lg p-4 mb-6 ${getStatusBgColor()}`}>
              <div className="flex items-start space-x-3">
                {getStatusIcon()}
                <div>
                  <p className="text-sm font-medium">{resetStatus.message}</p>
                </div>
              </div>
            </div>
          )}

          {clearStatus.message && (
            <div className={`border rounded-lg p-4 mb-6 ${getStatusBgColor()}`}>
              <div className="flex items-start space-x-3">
                {getStatusIcon()}
                <div>
                  <p className="text-sm font-medium">{clearStatus.message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Admin Actions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Reset Demo Data Button */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Reset All Data</h3>
              <p className="text-xs text-gray-600 mb-3">Clears all data and creates fresh realistic demo data</p>
              <button
                onClick={handleResetData}
                disabled={resetLoading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {resetLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span className="text-sm">Reset Demo Data</span>
              </button>
            </div>

            {/* Clear All Data Button */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Clear All Data</h3>
              <p className="text-xs text-gray-600 mb-3">⚠️ Permanently delete ALL data from database</p>
              <button
                onClick={handleClearAllData}
                disabled={clearLoading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="⚠️ This will permanently delete ALL data!"
              >
                {clearLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span className="text-sm">Clear All Data</span>
              </button>
            </div>

            {/* View Statistics Button */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-2">View Statistics</h3>
              <p className="text-xs text-gray-600 mb-3">Show current data statistics across all systems</p>
              <button
                onClick={handleViewStats}
                disabled={statsLoading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {statsLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Info className="h-4 w-4" />
                )}
                <span className="text-sm">View Statistics</span>
              </button>
            </div>
          </div>

          {/* DT Orders Section */}
          <div className="border-t border-gray-200 pt-6 mt-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <Package className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-blue-800">Create Realistic DT Orders</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Generate realistic Discount Tire orders with tires, services, and real issue patterns 
                    (Finance posting errors, Missing orders, Payment mismatches, IDOC failures).
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Generate DT Orders</h3>
                <p className="text-sm text-gray-600">
                  Creates realistic DT orders with tire products, services, and cross-system discrepancies
                </p>
              </div>
              <button
                onClick={handleCreateDTOrders}
                disabled={resetLoading}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {resetLoading ? (
                  <>
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Package className="h-5 w-5" />
                    <span>Create DT Orders</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Display */}
        {statsData && (
          <div className="border-t border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 System Statistics</h3>
            
            {statsData.error ? (
              <div className="text-red-600 bg-red-50 p-4 rounded-lg">{statsData.error}</div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="text-sm text-blue-600 font-medium">Total Orders</div>
                    <div className="text-2xl font-bold text-blue-900">{statsData.total_orders}</div>
                  </div>
                  {Object.entries(statsData.systems || {}).map(([systemName, systemData]: [string, any]) => (
                    <div key={systemName} className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-600 capitalize font-medium">{systemName}</div>
                      <div className="text-2xl font-bold text-gray-900">{systemData.order_count}</div>
                      <div className="text-xs text-gray-500">orders</div>
                    </div>
                  ))}
                </div>

                {/* Order List by System */}
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">Orders by System</h4>
                  <div className="space-y-3">
                    {Object.entries(statsData.systems || {}).map(([systemName, systemData]: [string, any]) => (
                      <div key={systemName} className="bg-gray-50 p-3 rounded border">
                        <div className="font-medium text-sm text-gray-900 mb-2 capitalize">
                          {systemName} ({systemData.order_count} orders)
                        </div>
                        {systemData.orders.length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {systemData.orders.slice(0, 6).map((orderId: string) => (
                              <div key={orderId} className="text-xs font-mono bg-white px-2 py-1 rounded border">
                                {orderId}
                              </div>
                            ))}
                            {systemData.orders.length > 6 && (
                              <div className="text-xs text-gray-500 px-2 py-1">
                                +{systemData.orders.length - 6} more...
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">No orders</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* LLM Configuration Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Cpu className="h-6 w-6 text-purple-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">LLM Configuration</h2>
              <p className="text-sm text-gray-600">Select the AI provider and model used by agents at runtime</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {llmLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-5 w-5 animate-spin text-gray-400 mr-2" />
              <span className="text-sm text-gray-500">Loading configuration...</span>
            </div>
          ) : llmConfig ? (
            <div className="space-y-6">
              {/* Provider Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(llmConfig.available_providers).map(([providerKey, providerInfo]) => (
                  <button
                    key={providerKey}
                    onClick={() => {
                      setSelectedProvider(providerKey);
                      const models = providerInfo.models;
                      if (providerKey === llmConfig.active_provider) {
                        setSelectedModel(llmConfig.active_model);
                      } else if (models.length > 0) {
                        setSelectedModel(models[0].id);
                      }
                    }}
                    className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                      selectedProvider === providerKey
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900 capitalize">{providerKey}</span>
                      {providerInfo.configured ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Key Set
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          No Key
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {providerInfo.models.length} model{providerInfo.models.length !== 1 ? 's' : ''} available
                    </div>
                    {selectedProvider === providerKey && (
                      <div className="absolute top-2 right-2">
                        <div className="w-3 h-3 rounded-full bg-purple-500" />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition bg-white"
                >
                  {availableModels.map((m) => (
                    <option key={m.id} value={m.id}>{m.name} ({m.id})</option>
                  ))}
                </select>
              </div>

              {/* No key warning */}
              {!providerHasKey && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-yellow-700">
                    No API key configured for <strong>{selectedProvider}</strong>. Add <code>{selectedProvider === 'openai' ? 'OPENAI_API_KEY' : 'ANTHROPIC_API_KEY'}</code> to your environment variables. Agents will use mock responses until a key is set.
                  </p>
                </div>
              )}

              {/* Current active display */}
              <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                <div className="text-sm">
                  <span className="text-gray-500">Currently active:</span>{' '}
                  <span className="font-medium text-gray-900 capitalize">{llmConfig.active_provider}</span>
                  <span className="text-gray-400 mx-1">/</span>
                  <span className="font-mono text-sm text-purple-700">{llmConfig.active_model}</span>
                </div>
                {(selectedProvider !== llmConfig.active_provider || selectedModel !== llmConfig.active_model) && (
                  <span className="text-xs text-orange-600 font-medium">Unsaved changes</span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSaveLlmConfig}
                  disabled={llmSaving || (selectedProvider === llmConfig.active_provider && selectedModel === llmConfig.active_model)}
                  className="flex items-center space-x-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  {llmSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  <span>Save Configuration</span>
                </button>
                <button
                  onClick={handleTestLlm}
                  disabled={llmTesting}
                  className="flex items-center space-x-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  {llmTesting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                  <span>Test Connection</span>
                </button>
              </div>

              {/* Test Result */}
              {llmTestResult && (
                <div className={`rounded-lg p-4 border ${
                  llmTestResult.success
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start space-x-2">
                    {llmTestResult.success ? (
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                    )}
                    <div>
                      <div className={`text-sm font-medium ${llmTestResult.success ? 'text-green-800' : 'text-red-800'}`}>
                        {llmTestResult.success ? 'Connection Successful' : 'Connection Failed'}
                      </div>
                      <p className={`text-xs mt-1 ${llmTestResult.success ? 'text-green-700' : 'text-red-700'}`}>
                        {llmTestResult.message}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Failed to load LLM configuration.</p>
          )}
        </div>
      </div>

      {/* System Info Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Info className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">System Information</h2>
              <p className="text-sm text-gray-600">Current system status and configuration</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Active Systems</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="text-sm">GKPOS Simulator</span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="text-sm">SAP ECC Simulator</span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="text-sm">Hybris Simulator</span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-gray-900 mb-3">AI Agents</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="text-sm">Monitor Agent</span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Idle
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="text-sm">Verify Agent</span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Idle
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="text-sm">Execute Agent</span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Idle
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="text-sm">Learn Agent</span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Idle
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={modal.open}
        onClose={() => setModal(m => ({ ...m, open: false }))}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
      />
    </div>
  );
};

export default AdminPage;
